/**
 * Turning Tesseract's output into paragraphs, and deciding when a page was read badly enough to
 * try again with a different page-segmentation mode. Pure and unit-tested (Tesseract itself can't
 * run in the test environment); the structural types below match what tesseract.js returns.
 */

export interface OcrLineLike {
  text: string;
  bbox?: { x0: number; y0: number; x1: number; y1: number };
}
export interface OcrParagraphLike {
  text?: string;
  lines?: OcrLineLike[];
}
export interface OcrPageLike {
  text?: string;
  confidence?: number;
  blocks?: { paragraphs?: OcrParagraphLike[] }[] | null;
}

const clean = (s: string) => s.replace(/[ \t]+/g, " ").trim();

const LIST_START = /^(\d{1,3}[.)]|\([a-z0-9]{1,3}\)|[\u2022\-\u2013*])\s+\S/i;
const endsTerminal = (t: string) => /[.!?:;,)"\u201D\u2019]\s*$/.test(t);
const startsLower = (t: string) => /^[a-z\u00DF-\u00FF]/.test(t);

/** Where a new paragraph starts between two lines of one OCR paragraph: a numbered / bulleted line, a clear vertical gap, or a short line followed by a line that starts a new sentence. */
function breaksBetween(prev: OcrLineLike, cur: OcrLineLike, ctx: { right: number; width: number; lineHeight: number }): boolean {
  if (LIST_START.test(cur.text.trim())) return true;
  if (prev.bbox && cur.bbox) {
    if (cur.bbox.y0 - prev.bbox.y1 > 0.9 * ctx.lineHeight) return true;
    if (prev.bbox.x1 < ctx.right - 0.15 * ctx.width && (endsTerminal(prev.text.trim()) || !startsLower(cur.text.trim()))) return true;
  }
  return false;
}

/** Real paragraphs: Tesseract's own paragraph grouping, split further where the line geometry says a paragraph ended (or the text where no geometry is given). Lines inside a paragraph are joined with a space. */
export function ocrParagraphs(page: OcrPageLike): string[] {
  const paras: OcrLineLike[][] = [];
  for (const b of page.blocks ?? []) {
    for (const p of b.paragraphs ?? []) {
      const lines = p.lines?.length ? p.lines : (p.text ?? "").split("\n").map((t) => ({ text: t }));
      const kept = lines.filter((l) => l.text.trim());
      if (kept.length) paras.push(kept);
    }
  }
  if (!paras.length) {
    for (const chunk of (page.text ?? "").split(/\n\s*\n/)) {
      const kept = chunk.split("\n").map((t) => ({ text: t })).filter((l) => l.text.trim());
      if (kept.length) paras.push(kept);
    }
  }
  const all = paras.flat();
  const withBox = all.filter((l) => l.bbox);
  const heights = withBox.map((l) => l.bbox!.y1 - l.bbox!.y0).sort((a, b) => a - b);
  const ctx = {
    right: withBox.length ? Math.max(...withBox.map((l) => l.bbox!.x1)) : 0,
    width: withBox.length ? Math.max(...withBox.map((l) => l.bbox!.x1)) - Math.min(...withBox.map((l) => l.bbox!.x0)) : 0,
    lineHeight: heights.length ? heights[Math.floor(heights.length / 2)] : 0,
  };
  const out: string[] = [];
  for (const lines of paras) {
    let cur: string[] = [];
    lines.forEach((ln, i) => {
      if (i > 0 && breaksBetween(lines[i - 1], ln, ctx)) {
        out.push(clean(cur.join(" ")));
        cur = [];
      }
      cur.push(ln.text.trim());
    });
    if (cur.length) out.push(clean(cur.join(" ")));
  }
  return out.filter(Boolean);
}

/** Fraction of lines that are tiny fragments (under 6 characters): the signature of a page whose columns were mis-segmented. */
export function fragmentFraction(text: string): number {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  if (lines.length < 6) return 0;
  return lines.filter((l) => l.replace(/\s/g, "").length < 6).length / lines.length;
}

/** Share of letters that are Latin / digits / punctuation, i.e. how English-looking the text is (for the "wrong script crept in" case). */
export function foreignScriptFraction(text: string, allowDevanagari: boolean): number {
  const LETTER = new RegExp("\\p{L}", "u");
  const letters = Array.from(text).filter((c) => LETTER.test(c));
  if (!letters.length) return 0;
  const odd = letters.filter((c) => (allowDevanagari ? false : /[ऀ-ॿ]/.test(c))).length;
  return odd / letters.length;
}

/** One number for "how good is this reading": higher is better. */
export function readingScore(page: OcrPageLike, allowDevanagari: boolean): number {
  const text = page.text ?? "";
  const conf = typeof page.confidence === "number" ? page.confidence : 60;
  return conf - 100 * fragmentFraction(text) - 100 * foreignScriptFraction(text, allowDevanagari);
}

/** Whether to read the page again as one block of text. */
export function needsSecondReading(page: OcrPageLike, allowDevanagari: boolean): boolean {
  const text = page.text ?? "";
  return fragmentFraction(text) > 0.2 || foreignScriptFraction(text, allowDevanagari) > 0.02 || (page.confidence ?? 100) < 60;
}

export const OCR_LANGUAGES: { id: string; label: string }[] = [
  { id: "eng", label: "English" },
  { id: "eng+hin", label: "English + Hindi" },
  { id: "hin", label: "Hindi" },
  { id: "spa", label: "Spanish" },
  { id: "fra", label: "French" },
  { id: "deu", label: "German" },
  { id: "por", label: "Portuguese" },
  { id: "ita", label: "Italian" },
];

export const allowsDevanagari = (lang: string) => lang.split("+").includes("hin");
